const runtimeApiBase = typeof window !== "undefined"
  ? window.__RUNTIME_CONFIG__?.REACT_APP_API_BASE
  : "";

const localApiFallback = typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(String(window.location?.hostname || ""))
  ? "http://localhost:4000"
  : "";

export const API_BASE = String(
  runtimeApiBase || process.env.REACT_APP_API_BASE || process.env.REACT_APP_DEFAULT_API_BASE || localApiFallback || ""
).trim().replace(/\/$/, "");

const API_BASE_MISSING_ERROR = "API base URL is not configured. Set REACT_APP_API_BASE in Cloud Run service environment variables (runtime), then redeploy the frontend revision.";
let unauthorizedHandler = null;

export const getAuthToken = () => localStorage.getItem("dw-auth-token") || "";

export const setAuthSession = (token, user) => {
  if (!token || !user) return;
  localStorage.setItem("dw-auth-token", token);
  localStorage.setItem("dw-auth-user", JSON.stringify(user));
};

export const clearAuthSession = () => {
  localStorage.removeItem("dw-auth-token");
  localStorage.removeItem("dw-auth-user");
};

export const setUnauthorizedHandler = handler => {
  unauthorizedHandler = typeof handler === "function" ? handler : null;
};

export const getStoredUser = () => {
  const raw = localStorage.getItem("dw-auth-user");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const apiFetch = async (path, options = {}) => {
  if (!API_BASE) {
    throw new Error(API_BASE_MISSING_ERROR);
  }

  const { timeoutMs, ...fetchOptions } = options || {};
  const token = getAuthToken();
  const headers = { ...(fetchOptions.headers || {}) };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const isFormData = typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;
  if (fetchOptions.body && !isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller && Number(timeoutMs) > 0
    ? setTimeout(() => controller.abort(), Number(timeoutMs))
    : null;

  try {
    return await fetch(`${API_BASE}${path}`, {
      ...fetchOptions,
      headers,
      signal: fetchOptions.signal || controller?.signal
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const apiJson = async (path, options = {}) => {
  let res;
  try {
    res = await apiFetch(path, options);
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Request timed out");
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401) {
      clearAuthSession();
      if (unauthorizedHandler) unauthorizedHandler();
    }
    const message = data?.error || `Request failed with status ${res.status}`;
    throw new Error(message);
  }
  return data;
};

export const apiPublicFetch = async (path, options = {}) => {
  if (!API_BASE) {
    throw new Error(API_BASE_MISSING_ERROR);
  }

  const { timeoutMs, ...fetchOptions } = options || {};
  const headers = { ...(fetchOptions.headers || {}) };

  const isFormData = typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;
  if (fetchOptions.body && !isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller && Number(timeoutMs) > 0
    ? setTimeout(() => controller.abort(), Number(timeoutMs))
    : null;

  try {
    return await fetch(`${API_BASE}${path}`, {
      ...fetchOptions,
      headers,
      signal: fetchOptions.signal || controller?.signal
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const apiPublicJson = async (path, options = {}) => {
  let res;
  try {
    res = await apiPublicFetch(path, options);
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Request timed out");
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message = data?.error || `Request failed with status ${res.status}`;
    throw new Error(message);
  }
  return data;
};
